import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const AdminRoomTypes = () => {
  const { token } = useAuth();
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_price: '',
    max_guests: '',
    amenities: '',
    images: '',
  });

  useEffect(() => {
    loadRoomTypes();
  }, []);

  const loadRoomTypes = async () => {
    try {
      const data = await api.get('/api/room-types');
      setRoomTypes(data);
    } catch (error) {
      toast.error('Erro ao carregar tipos de quartos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        base_price: parseFloat(formData.base_price),
        max_guests: parseInt(formData.max_guests),
        amenities: formData.amenities.split(',').map(a => a.trim()),
        images: formData.images.split(',').map(i => i.trim()),
      };

      if (editingId) {
        await api.put(`/api/admin/room-types/${editingId}`, payload, token);
        toast.success('Tipo de quarto atualizado!');
      } else {
        await api.post('/api/admin/room-types', payload, token);
        toast.success('Tipo de quarto criado!');
      }

      setDialogOpen(false);
      resetForm();
      loadRoomTypes();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar');
    }
  };

  const handleEdit = (roomType) => {
    setEditingId(roomType.id);
    setFormData({
      name: roomType.name,
      description: roomType.description,
      base_price: roomType.base_price.toString(),
      max_guests: roomType.max_guests.toString(),
      amenities: roomType.amenities.join(', '),
      images: roomType.images.join(', '),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este tipo de quarto?')) return;
    try {
      await api.delete(`/api/admin/room-types/${id}`, token);
      toast.success('Tipo de quarto excluído!');
      loadRoomTypes();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      base_price: '',
      max_guests: '',
      amenities: '',
      images: '',
    });
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-room-types-page">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-display font-bold text-primary">
            Tipos de Quartos
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-room-type-button">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Tipo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar' : 'Adicionar'} Tipo de Quarto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nome</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="room-type-name-input"
                  />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <textarea
                    required
                    rows="3"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="room-type-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Preço Base (R$)</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      value={formData.base_price}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      data-testid="room-type-price-input"
                    />
                  </div>
                  <div>
                    <Label>Máx. Hóspedes</Label>
                    <Input
                      required
                      type="number"
                      value={formData.max_guests}
                      onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
                      data-testid="room-type-guests-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Comodidades (separadas por vírgula)</Label>
                  <Input
                    required
                    value={formData.amenities}
                    onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                    placeholder="Wi-Fi, TV, Ar-condicionado"
                    data-testid="room-type-amenities-input"
                  />
                </div>
                <div>
                  <Label>URLs das Imagens (separadas por vírgula)</Label>
                  <Input
                    required
                    value={formData.images}
                    onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                    placeholder="https://..., https://..."
                    data-testid="room-type-images-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="save-room-type-button">
                  {editingId ? 'Atualizar' : 'Criar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {roomTypes.map((roomType) => (
              <div key={roomType.id} className="bg-white p-6 rounded-xl shadow-lg" data-testid={`room-type-card-${roomType.id}`}>
                <img
                  src={roomType.images[0]}
                  alt={roomType.name}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-display font-semibold text-primary mb-2">
                  {roomType.name}
                </h3>
                <p className="text-text-secondary text-sm mb-3">{roomType.description}</p>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold text-accent">R$ {roomType.base_price.toFixed(2)}</span>
                  <span className="text-sm text-text-muted">Até {roomType.max_guests} hóspedes</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleEdit(roomType)}
                    data-testid={`edit-room-type-${roomType.id}`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(roomType.id)}
                    data-testid={`delete-room-type-${roomType.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && roomTypes.length === 0 && (
          <div className="text-center py-12 text-text-secondary">
            Nenhum tipo de quarto cadastrado.
          </div>
        )}
      </div>
    </div>
  );
};
